import { HttpInterceptorFn, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { catchError, throwError, tap } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { inject } from '@angular/core';
import { CookieService } from '../../shared/services/cookie.service';
import { ApplicationContextService } from '../../shared/services/application-context.service';
import { ExceptionHandlerService, ApiErrorPayload } from '../services/exception-handler.service';
import { Router } from '@angular/router';
import { API_ENDPOINTS } from '../services/api.service';

/** Extract customer id from current route when on customer details (e.g. /main/customer/123/...). */
function getCustomerIdFromRoute(router: Router): string | null {
  const url = router.routerState.snapshot.url;
  const match = url.match(/customer\/(\d+)(?:\/|$)/);
  return match ? match[1] : null;
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const commonSvc = inject(CommonService);
  const cookieService = inject(CookieService);
  const applicationContextSvc = inject(ApplicationContextService);
  const exceptionHandler = inject(ExceptionHandlerService);
  const router = inject(Router);

  // Read token from cookie instead of localStorage
  const token = cookieService.getCookie('token');
  const selectedApplicationId = applicationContextSvc.getSelectedApplicationId();

  const headers: { [key: string]: string } = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (selectedApplicationId) {
    headers['x-app-id'] = selectedApplicationId.toString();
  }

  // Add x-customer-id for all operations on the customer details page, except customer add
  const isCustomerAdd = req.url.includes(API_ENDPOINTS.CUSTOMER.ADD);
  if (!isCustomerAdd) {
    const customerId = getCustomerIdFromRoute(router);
    if (customerId) {
      headers['x-customer-id'] = customerId;
    }
  }

  if (Object.keys(headers).length > 0) {
    req = req.clone({
      setHeaders: headers,
    });
  }

  return next(req).pipe(
    // Handle business errors encoded in successful HTTP responses (e.g. status 1004 in body)
    tap((event: HttpEvent<any>) => {
      if (event.type === HttpEventType.Response) {
        const body = (event as any).body as ApiErrorPayload | undefined;
        if (body && typeof body.status === 'number') {
          exceptionHandler.handleHttpError(body);
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      const payload = error.error as ApiErrorPayload | undefined;

      // Network/connection issues
      if (error.status === 0) {
        commonSvc.message.error('Server is down or unreachable.');
      }
      // Auth errors
      else if (error.status === 401 || error.status === 403) {
        commonSvc.message.error('Unauthorized request. Please log in again.');
      }
      // HTTP status itself is 1004 – treat as business error and show its message
      else if (error.status === 1004) {
        exceptionHandler.handleHttpError({
          status: error.status,
          message: (error.error as any)?.message || error.message,
          data: (error.error as any)?.data
        });
      }
      // API sent a structured business error payload (e.g. status 1004) in the error body
      else if (payload && typeof payload.status === 'number') {
        exceptionHandler.handleHttpError(payload);
      }
      // Fallback generic HTTP error
      else {
        console.log(`HTTP Error: ${error.status} - ${error.message}`);
        commonSvc.message.error(
          `HTTP Error code: ${error.status} - Something went wrong please contact support.`,
          { nzDuration: 4000, nzPauseOnHover: true }
        );
      }

      return throwError(() => error);
    })
  );
};
